import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import CookieBanner from "@/components/CookieBanner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CostsProvider } from "@/context/CostsContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Keywords from "@/pages/Keywords";
import SiteAudit from "@/pages/SiteAudit";
import AuditReport from "@/pages/AuditReport";
import ContentAEO from "@/pages/ContentAEO";
import Backlinks from "@/pages/Backlinks";
import Competitors from "@/pages/Competitors";
import LocalSEO from "@/pages/LocalSEO";
import Reports from "@/pages/Reports";
import Cannibalization from "@/pages/Cannibalization";
import Referrals from "@/pages/Referrals";
import Settings from "@/pages/Settings";
import PublicShare from "@/pages/PublicShare";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import Glossary from "@/pages/Glossary";
import "@/App.css";

function Protected({ children }) {
    const { user, loading } = useAuth();
    const loc = useLocation();
    if (loading) return <div className="min-h-screen grid place-items-center text-muted-foreground font-mono text-sm">loading…</div>;
    if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
    return children;
}

function ScrollTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function AppRouter() {
    // Google OAuth callback: session_id arrives in the URL fragment. This
    // synchronous check (via useLocation, NOT window.location) must run
    // BEFORE any protected-route auth check to avoid race conditions.
    const location = useLocation();
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
        <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/share/:token" element={<PublicShare />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/payment/success" element={<PaymentSuccess />} />
                    <Route path="/payment/cancel" element={<PaymentCancel />} />
                    <Route path="/app" element={<Protected><AppShell /></Protected>}>
                        <Route index element={<Dashboard />} />
                        <Route path="keywords" element={<Keywords />} />
                        <Route path="audit" element={<SiteAudit />} />
                        <Route path="audit/report" element={<AuditReport />} />
                        <Route path="content" element={<ContentAEO />} />
                        <Route path="backlinks" element={<Backlinks />} />
                        <Route path="competitors" element={<Competitors />} />
                        <Route path="local-seo" element={<LocalSEO />} />
                        <Route path="cannibalization" element={<Cannibalization />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="referrals" element={<Referrals />} />
                        <Route path="glossary" element={<Glossary />} />
                        <Route path="settings" element={<Settings />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    useEffect(() => {
        const ZOOM_LEVELS = [100, 115, 130, 150, 175];
        const stored = Number(localStorage.getItem("inphase_zoom_level"));
        if (ZOOM_LEVELS.includes(stored)) {
            document.documentElement.style.fontSize = `${(16 * stored) / 100}px`;
        }
    }, []);
    return (
        <AuthProvider>
            <CostsProvider>
            <TooltipProvider delayDuration={150}>
            <BrowserRouter>
                <ScrollTop />
                <Toaster theme="dark" position="bottom-right" richColors />
                <AppRouter />
                <CookieBanner />
            </BrowserRouter>
            </TooltipProvider>
            </CostsProvider>
        </AuthProvider>
    );
}
